/**
 * Legal / policy stub pages.
 *
 * These are placeholders with reasonable default language so the site can
 * accept payments without a legally empty footer. REPLACE THE BODY TEXT with
 * output from Termly / iubenda / your lawyer before going live with real
 * transactions — India's Consumer Protection (E-Commerce) Rules 2020 require
 * a published refund/cancellation policy on the same site as the checkout,
 * and Stripe's TOS require a privacy policy + terms link visible at checkout.
 *
 * All three pages share one layout component so updates stay consistent; we
 * export four named components (Terms / Privacy / Refund / Contact) that
 * App.tsx routes to.
 *
 * NOTE: "Last updated" is computed once at bundle time via Vite's build
 * constants so it matches the build's BUILD_TIME footer tag.
 */

import { Link } from 'react-router-dom';

const LAST_UPDATED = __BUILD_TIME__.slice(0, 10); // YYYY-MM-DD

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="container-wide py-12 max-w-3xl">
      <h1 className="text-3xl font-display font-bold">{title}</h1>
      <p className="text-xs text-ink-500 mt-1">Last updated: {LAST_UPDATED}</p>
      <div className="prose prose-ink mt-6 space-y-4 text-ink-700 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

export function Terms() {
  return (
    <Shell title="Terms of Service">
      <p>
        Welcome to GradeLab ("we", "us", "our"). By creating an account or using any part of
        the service at gradelab.club you agree to these Terms. If you do not agree, please do
        not use the service.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">1. Who can use GradeLab</h2>
      <p>
        You must be at least 13 years old (or the minimum age in your jurisdiction) and able
        to enter a binding contract. Enterprise / institutional users bind the organisation
        they represent.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">2. Your content</h2>
      <p>
        You retain full ownership of reports you upload or generate. We process your content
        solely to deliver the grading, generation, and rubric features you request. We do not
        use your content to train third-party models. Uploaded source files are retained in
        your private Firebase Storage namespace; generated markdown is retained for 7 days on
        free / try / starter plans and 30 days on Pro / Team plans (see our Retention policy
        in the Privacy page).
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Submit content you do not have the right to submit.</li>
        <li>Attempt to reverse-engineer, scrape, or rate-limit-bypass the service.</li>
        <li>Use GradeLab to produce content that violates applicable law, infringes IP, or
          to misrepresent AI-generated work as entirely your own in contexts where that is
          prohibited (e.g. most academic honour codes).</li>
      </ul>

      <h2 className="font-display font-semibold text-xl mt-8">4. AI output disclaimer</h2>
      <p>
        Grading, feedback, and generated text are produced by third-party large language
        models. Output may be inaccurate, incomplete, or biased. GradeLab is a drafting and
        review aid, not a substitute for human expert judgement. You are responsible for
        reviewing and verifying all output before relying on it.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">5. Payments &amp; credits</h2>
      <p>
        Credit packs are one-time purchases; Pro and Team are monthly subscriptions that
        auto-renew until cancelled. All prices are exclusive of applicable taxes. Payments
        are processed by Stripe; we do not store full card numbers. Charges from GradeLab
        will appear on your bank or card statement under our payments partner's name,
        <strong> PRIME AXIS</strong>, which is the registered merchant descriptor for
        GradeLab transactions.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">6. Referral program</h2>
      <p>
        Invite a friend to join GradeLab and you both receive 5 credits once they verify a
        phone number through our sign-up flow. You can earn a maximum of 50 credits
        (10 successful referrals) from the program. Referral codes are valid only on newly
        created accounts and must be attached within 14 days of sign-up.
      </p>
      <p>
        To receive a shareable referral link of your own, you must verify a phone number
        on your GradeLab account. Each phone number can activate the referral system on
        at most one GradeLab account, across all GradeLab accounts and across all roles
        (referrer or referee). This rule is how we keep the program fair and stop ring
        fraud; without it, a single person could spin up unlimited accounts and collect
        credits without doing any real sharing.
      </p>
      <p>
        We reserve the right to withhold or reverse referral credits where we reasonably
        suspect abuse — for example, rings of accounts created from a single device,
        disposable or virtual phone numbers, automated sign-ups, or patterns of use that are
        inconsistent with organic sharing. Credits earned through referrals have no cash
        value, cannot be transferred between accounts, and cannot be redeemed for cash. We
        may modify, suspend, or end the program at any time.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">7. Termination</h2>
      <p>
        You may close your account at any time. We may suspend accounts that violate these
        Terms or that pose a security/abuse risk, with reasonable notice except where urgency
        prevents it.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">8. Disclaimers &amp; liability</h2>
      <p>
        The service is provided "as is" without warranties of any kind. To the maximum extent
        permitted by law, GradeLab's total liability is limited to the fees you paid in the
        preceding 3 months.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">9. Governing law</h2>
      <p>
        These Terms are governed by the laws of Singapore, without regard to conflict-of-law
        principles. Disputes arising out of or in connection with these Terms will be
        submitted to the exclusive jurisdiction of the courts of Singapore, except that
        nothing in this clause prevents you from seeking relief in your local courts where
        mandatory consumer-protection laws of your country of residence grant you that right.
      </p>

      <p className="text-sm text-ink-500 mt-10">
        Questions? See our <Link to="/contact" className="underline">Contact page</Link> or
        email support.
      </p>
    </Shell>
  );
}

export function Privacy() {
  return (
    <Shell title="Privacy Policy">
      <p>
        This policy explains what data we collect, why, and how long we keep it. We follow
        the principle of collecting the minimum needed to run the service.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">Data we collect</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Account:</strong> your name, email, and profile image via Google
          sign-in, plus a Firebase user ID.</li>
        <li><strong>Usage:</strong> timestamps of evaluations / generations so we can enforce
          plan caps; no IP addresses are persisted beyond short-lived rate-limit state.</li>
        <li><strong>Content:</strong> reports you upload and markdown we generate, stored in
          your private Firebase Storage / Firestore namespace and visible only to you.</li>
        <li><strong>Payments:</strong> Stripe handles card data. We receive only the result
          (success / failure) and a customer reference.</li>
      </ul>

      <h2 className="font-display font-semibold text-xl mt-8">How long we keep it</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Account data: until you delete your account.</li>
        <li>Uploaded source files: until you delete them from History.</li>
        <li>Generated markdown: 7 days on free / try / starter plans, 30 days on Pro / Team.
          After that the markdown body is auto-deleted via Firestore TTL; the job metadata
          stays so your history count is accurate.</li>
        <li>Evaluation / rubric results: persisted until you delete them.</li>
      </ul>

      <h2 className="font-display font-semibold text-xl mt-8">Who we share with</h2>
      <p>
        We share the minimum necessary with three categories of processor, each bound by a
        DPA: Google (Firebase Auth / Firestore / Hosting / Cloud Run), the LLM provider
        (OpenAI or Anthropic, depending on server config) for the grading / generation
        calls, and Stripe for payments. No advertising partners, no data brokers, no sale
        of personal data.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">Your rights</h2>
      <p>
        You can access, export, correct, or delete your data at any time by emailing
        support, or (for deletion) by deleting your account. Requests are processed within
        30 days.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">Cookies &amp; local storage</h2>
      <p>
        We use Firebase Auth's session cookie and a small amount of local storage for
        signed-in state. No third-party tracking or advertising cookies are set.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">Children</h2>
      <p>
        The service is not directed at children under 13. If you believe a child has created
        an account, contact us and we will delete it.
      </p>
    </Shell>
  );
}

export function Refund() {
  return (
    <Shell title="Refund &amp; Cancellation Policy">
      <p>
        We want you to get value from GradeLab. If something went wrong, read on.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">Credit packs (Try, Starter)</h2>
      <p>
        Unused credits are refundable within <strong>14 days</strong> of purchase if fewer
        than 2 credits have been consumed on the pack. Email support with your order ID and
        we will refund the unused portion to the original payment method within 5–10
        business days.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">Subscriptions (Pro, Team)</h2>
      <p>
        You can cancel at any time from your account page. Cancellation takes effect at the
        end of the current billing period; the plan stays active until then and no further
        charges are made. Partial-month refunds are available within 7 days of the renewal
        date if no more than 10% of that month's operation cap has been used.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">Service outages</h2>
      <p>
        If an operation fails due to our error (e.g. the AI provider returns an invalid
        response), the credit is automatically refunded. You do not need to contact support
        for these.
      </p>

      <h2 className="font-display font-semibold text-xl mt-8">Non-refundable</h2>
      <p>
        Credits already consumed on a completed operation are not refundable solely because
        you disagreed with the AI's grading or generated text. Those are judgement calls,
        not service failures.
      </p>
    </Shell>
  );
}

export function Contact() {
  return (
    <Shell title="Contact">
      <p>
        We reply within one business day.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Support &amp; billing:</strong> support@gradelab.club</li>
        <li><strong>Security issues:</strong> security@gradelab.club (please do not post
          vulnerabilities publicly before we have had a chance to fix them)</li>
        <li><strong>Press / partnerships:</strong> hello@gradelab.club</li>
      </ul>
      <p className="text-sm text-ink-500 mt-10">
        GradeLab is available worldwide. We localise pricing and language to your
        region so the product feels at home wherever you study or teach.
      </p>
    </Shell>
  );
}
